import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';

interface SlotPickerProps {
  coachId: string;
  eventTypeId: string;
  durationMinutes: number;
  onSelect: (slotIso: string) => void;
  onBack: () => void;
  /** Auth token — provided from coach dashboard; omit for public booking page */
  authToken?: string;
}

/** Format ISO slot string to display-friendly time.
 * Input: UTC ISO string (e.g. "2030-01-07T09:00:00+00:00").
 * Output: local time string (e.g. "9:00 AM") rendered in the user's browser timezone. */
function formatSlotTime(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

/** Group slot ISO strings by local date string (YYYY-MM-DD).
 * Input: UTC ISO strings. Output: date-keyed map rendered in local timezone. */
function groupSlotsByDate(slots: string[]): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const slot of slots) {
    const dateKey = new Date(slot).toLocaleDateString('en-CA'); // YYYY-MM-DD in local TZ
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(slot);
  }
  return groups;
}

/** Returns the days to display in a 7-column calendar grid starting on Monday.
 * Null entries are padding cells before the first of the month. */
function buildCalendarDays(year: number, month: number): Array<Date | null> {
  const days: Array<Date | null> = [];
  const firstOfMonth = new Date(year, month, 1);
  const dow = firstOfMonth.getDay(); // 0=Sun … 6=Sat
  const padding = dow === 0 ? 6 : dow - 1; // cells before Monday
  for (let i = 0; i < padding; i++) days.push(null);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  for (let d = 1; d <= daysInMonth; d++) {
    days.push(new Date(year, month, d));
  }
  return days;
}

const DAY_HEADERS = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

const SlotPicker: React.FC<SlotPickerProps> = ({
  coachId,
  eventTypeId,
  durationMinutes,
  onSelect,
  onBack,
  authToken,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const [monthOffset, setMonthOffset] = useState(0);
  const [slots, setSlots] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const { displayYear, displayMonth } = useMemo(() => {
    const d = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
    return { displayYear: d.getFullYear(), displayMonth: d.getMonth() };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthOffset]);

  const monthLabel = new Date(displayYear, displayMonth, 1).toLocaleDateString([], {
    month: 'long',
    year: 'numeric',
  });

  // Fetch slots for the entire displayed month
  useEffect(() => {
    const monthStart = new Date(displayYear, displayMonth, 1);
    monthStart.setHours(0, 0, 0, 0);
    const monthEnd = new Date(displayYear, displayMonth + 1, 0);
    monthEnd.setHours(23, 59, 59, 999);

    const params = new URLSearchParams({
      event_type_id: eventTypeId,
      date_from: monthStart.toISOString(),
      date_to: monthEnd.toISOString(),
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    const headers: Record<string, string> = {};
    if (authToken) headers['Authorization'] = `Bearer ${authToken}`;

    setLoading(true);
    setError('');
    setSlots([]);
    fetch(`${config.apiBaseUrl}/book/${coachId}/slots?${params}`, { headers })
      .then(r => {
        if (!r.ok) throw new Error('Failed');
        return r.json();
      })
      .then(data => setSlots(data.slots ?? []))
      .catch(() => setError('Could not load available times. Please try again.'))
      .finally(() => setLoading(false));
  }, [coachId, eventTypeId, displayYear, displayMonth, authToken]);

  useEffect(() => { setSelectedDate(null); }, [displayYear, displayMonth]);

  const slotsByDate = useMemo(() => groupSlotsByDate(slots), [slots]);
  const calendarDays = useMemo(
    () => buildCalendarDays(displayYear, displayMonth),
    [displayYear, displayMonth],
  );

  const selectedSlots = selectedDate ? (slotsByDate[selectedDate] ?? []) : [];

  const selectedDateLabel = selectedDate
    ? new Date(selectedDate + 'T12:00:00').toLocaleDateString([], {
        weekday: 'long', month: 'long', day: 'numeric',
      })
    : null;

  return (
    <div className="flex-1 flex items-start justify-center py-8 px-4">
      <div className="w-full max-w-sm bg-dark-card border border-dark-border rounded-2xl shadow-xl overflow-hidden">

        {/* Duration + back */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border">
          <button onClick={onBack} className="text-xs text-gray-500 hover:text-gray-300 transition-colors">
            ← Back
          </button>
          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
            <ClockIcon className="w-3.5 h-3.5" /> {durationMinutes} min
          </span>
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-dark-border">
          <button
            onClick={() => setMonthOffset(m => m - 1)}
            disabled={monthOffset <= 0}
            className="p-1 rounded text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
          <span className="text-sm font-medium text-gray-200">{monthLabel}</span>
          <button
            onClick={() => setMonthOffset(m => m + 1)}
            className="p-1 rounded text-gray-400 hover:text-white transition-colors"
          >
            <ChevronRightIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Calendar grid */}
        <div className="px-3 pt-2 pb-1">
          {/* Day-of-week headers */}
          <div className="grid grid-cols-7 mb-1">
            {DAY_HEADERS.map(d => (
              <div key={d} className="text-center text-xs font-medium text-gray-600 py-1">{d}</div>
            ))}
          </div>

          {/* Day cells */}
          <div className="grid grid-cols-7 gap-y-0.5">
            {calendarDays.map((date, i) => {
              if (!date) return <div key={`pad-${i}`} />;
              const dateKey = date.toLocaleDateString('en-CA');
              const isPast = date < today;
              const hasSlots = !loading && !!slotsByDate[dateKey];
              const isSelected = dateKey === selectedDate;
              const isToday = dateKey === today.toLocaleDateString('en-CA');

              return (
                <button
                  key={dateKey}
                  disabled={isPast || (!loading && !hasSlots)}
                  onClick={() => setSelectedDate(dateKey)}
                  className={[
                    'relative flex items-center justify-center rounded-lg h-8 text-xs transition-colors',
                    isSelected
                      ? 'bg-primary-600 text-white font-semibold'
                      : hasSlots
                      ? 'text-gray-200 hover:bg-dark-hover cursor-pointer font-medium'
                      : 'text-gray-700 cursor-not-allowed',
                    isToday && !isSelected ? 'ring-1 ring-inset ring-primary-500' : '',
                  ].join(' ')}
                >
                  {date.getDate()}
                  {hasSlots && !isSelected && (
                    <span className="absolute bottom-0.5 w-1 h-1 rounded-full bg-primary-400" />
                  )}
                </button>
              );
            })}
          </div>

          {loading && (
            <div className="flex justify-center py-2">
              <div className="animate-spin w-3.5 h-3.5 border-2 border-primary-500 border-t-transparent rounded-full" />
            </div>
          )}
        </div>

        {/* Time slots */}
        {error ? (
          <div className="border-t border-dark-border px-4 py-3">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        ) : selectedDate ? (
          <div className="border-t border-dark-border px-4 py-3">
            <p className="text-xs font-medium text-gray-400 mb-2">{selectedDateLabel}</p>
            {selectedSlots.length === 0 ? (
              <p className="text-xs text-gray-500">No available times on this date.</p>
            ) : (
              <div className="max-h-36 overflow-y-auto pr-1">
                <div className="flex flex-wrap gap-1.5">
                  {selectedSlots.map(slot => (
                    <button
                      key={slot}
                      onClick={() => onSelect(slot)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium bg-dark-canvas border border-dark-border hover:border-primary-500 hover:bg-dark-hover text-gray-200 hover:text-white transition-colors"
                    >
                      {formatSlotTime(slot)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="border-t border-dark-border px-4 py-3">
            <p className="text-xs text-gray-500 text-center">
              {loading ? 'Loading availability…' : 'Select a date to see available times'}
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default SlotPicker;
